import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Placeholder, SortableTableHeader } from 'components';
import LotteryDrawItem from 'features/lotteryDraws/LotteryDrawItem';
import LotteryDrawToolbar from 'features/lotteryDraws/LotteryDrawToolbar';
import SearchForm from 'features/lotteryDraws/SearchForm';
import { useAlerts, useNavigation, useLotteryDraws, useSelected } from 'hooks';

const TABLE_HEADERS = [
	{
		value: 'user_id',
		label: 'User',
		sortable: true,
		hidden: false
	},
	{
		value: 'status',
		label: 'Status',
		sortable: true,
		hidden: false
	},
	{
		value: 'created_at',
		label: 'Created On',
		sortable: true,
		hidden: false
	}
];

const LotteryDrawList = ({ ...props }) => {
	const navigate = useNavigate();
	const status = 'active';

	const {
		isLoading,
		isEmpty,
		lotteryDraws,
		findLotteryDraws,
		deleteLotteryDraw,
		paginate,
		query,
		page,
		numPages,
		reloadLotteryDraws,
		sortKey,
		sortDirection,
		handleSort
	} = useLotteryDraws({});

	const searchParams = React.useMemo(
		() => ({
			'lottery_draws.status': status
		}),
		[status]
	);

	const { handleClick, handleEditClick } = useNavigation({
		history,
		url: '/lottery_draws'
	});

	const { selectedIds, handleSelect, selected, handleClear, totalSelected } =
		useSelected();

	const { showAlertSuccess } = useAlerts();

	const handleDeleteClick = async (lotteryDraw) => {
		await deleteLotteryDraw(lotteryDraw);
		showAlertSuccess('LotteryDraw has been deleted');
		reloadLotteryDraws();
	};

	React.useEffect(() => {
		findLotteryDraws(
			{
				...searchParams,
				sort_key: sortKey,
				sort_direction: sortDirection
			},
			page
		);
	}, [searchParams, page, sortKey, sortDirection]);

	return (
		<>
			<Container maxWidth="xl" className="custom-Mul-Container">
				<>
					<Paper>
						<SearchForm
							isLoading={isLoading}
							query={query}
							handleSearch={findLotteryDraws}
						/>
						{!isEmpty ? (
							<TableContainer component={Paper} elevation={0}>
								<Table>
									<TableHead>
										<TableRow>
											{TABLE_HEADERS.filter((h) => !h.hidden).map(
												(header, idx) => (
													<SortableTableHeader
														key={idx}
														handleSort={handleSort}
														value={`lottery_draws.${header.value}`}
														sortable={header.sortable}
														sortKey={sortKey}
														sortDirection={sortDirection}
													>
														{header.label}
													</SortableTableHeader>
												)
											)}
											<TableCell></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{lotteryDraws.map((lotteryDraw, idx) => (
											<LotteryDrawItem
												key={idx}
												lotteryDraw={lotteryDraw}
												isSelectable={false}
												selectedIds={selectedIds}
												handleSelect={handleSelect}
												handleClick={handleClick}
												handleDeleteClick={handleDeleteClick}
												handleEditClick={handleEditClick}
											/>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Placeholder
								icon={<Search />}
								title="No lottery draws"
								subtitle="Please try another search"
							/>
						)}
					</Paper>
					{!isEmpty && (
						<Box my={1}>
							<Pagination
								onChange={(ev, page) => paginate(page)}
								color="primary"
								size="small"
								shape="rounded"
								page={page}
								count={numPages}
							/>
						</Box>
					)}
				</>
				<LotteryDrawToolbar
					selected={selected}
					selectedIds={selectedIds}
					handleClear={handleClear}
					totalSelected={totalSelected}
				/>
			</Container>
		</>
	);
};

export default LotteryDrawList;
